import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { LTText } from '@lambdatestincprivate/lt-components';
import { parseConsoleLine } from '../../../model/ndjson';
import { formatBytes } from '../../format';
import styles from './dock.module.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var ROW_ESTIMATE_PX = 24;
var OVERSCAN = 12;
var LEVEL_COLOR = {
  error: 'var(--lt-text-error)',
  severe: 'var(--lt-text-error)',
  warning: 'var(--lt-text-warning)',
  warn: 'var(--lt-text-warning)',
  info: 'var(--lt-text-info)',
  debug: 'var(--lt-text-secondary)',
  log: 'var(--lt-text-secondary)'
};

/**
 * Console log rows (browser console ndjson), virtualized. The store holds
 * RAW lines; each row is JSON.parsed lazily, once, when it first scrolls
 * into view. Level column colored like kaneai's console renderer.
 */
export function ConsoleLogViewer(_ref) {
  var log = _ref.log;
  // Scroll element in STATE, not a ref: react-virtual 3.0.4 never
  // re-measures a scroll element that was null on the first render, and
  // only dev StrictMode's double render hides that.
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    scrollEl = _useState2[0],
    setScrollEl = _useState2[1];
  var rows = useMemo(function () {
    return log.lines.filter(function (l) {
      return !l.includes('"marker"');
    });
  }, [log.lines]);
  var parsedRef = useRef([]);
  if (parsedRef.current.length !== rows.length) {
    parsedRef.current = new Array(rows.length).fill(null);
  }
  var virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: function getScrollElement() {
      return scrollEl;
    },
    estimateSize: function estimateSize() {
      return ROW_ESTIMATE_PX;
    },
    overscan: OVERSCAN
  });
  if (log.phase === 'loading') {
    return /*#__PURE__*/_jsx("div", {
      className: styles.logStates,
      "aria-busy": "true",
      children: Array.from({
        length: 5
      }, function (_, i) {
        return /*#__PURE__*/_jsx("span", {
          className: styles.logSkeletonBar,
          "aria-hidden": "true"
        }, i);
      })
    });
  }
  if (log.phase === 'missing' || log.phase === 'ready' && rows.length === 0) {
    return /*#__PURE__*/_jsx("div", {
      className: styles.logStates,
      children: /*#__PURE__*/_jsx(LTText, {
        text: "No console logs were recorded for this test.",
        variant: LTText.TextVariant.SMALL_REGULAR,
        color: "#8c959f"
      })
    });
  }
  if (log.phase === 'too-large') {
    return /*#__PURE__*/_jsx("div", {
      className: styles.logStates,
      children: /*#__PURE__*/_jsx(LTText, {
        text: "This log is ".concat(log.totalBytes !== null ? formatBytes(log.totalBytes) : 'too large', " \u2014 too large to display in the viewer."),
        variant: LTText.TextVariant.SMALL_REGULAR,
        color: "#8c959f"
      })
    });
  }
  if (log.phase === 'error') {
    return /*#__PURE__*/_jsx("div", {
      className: styles.logStates,
      children: /*#__PURE__*/_jsx(LTText, {
        text: "Couldn't read this log from the pack.",
        variant: LTText.TextVariant.SMALL_REGULAR,
        color: "#8c959f"
      })
    });
  }
  var items = virtualizer.getVirtualItems();
  return /*#__PURE__*/_jsx("div", {
    ref: setScrollEl,
    className: styles.logScroll,
    children: /*#__PURE__*/_jsx("ul", {
      className: styles.logList,
      style: {
        height: virtualizer.getTotalSize()
      },
      children: items.map(function (item) {
        var _parsedRef$current, _item$index, _parsedRef$current$_i, _LEVEL_COLOR$entry$le;
        var entry = (_parsedRef$current$_i = (_parsedRef$current = parsedRef.current)[_item$index = item.index]) !== null && _parsedRef$current$_i !== void 0 ? _parsedRef$current$_i : _parsedRef$current[_item$index] = parseConsoleLine(rows[item.index], item.index);
        if (!entry) return null;
        return /*#__PURE__*/_jsxs("li", {
          ref: virtualizer.measureElement,
          "data-index": item.index,
          className: styles.logRow,
          "aria-setsize": rows.length,
          "aria-posinset": item.index + 1,
          style: {
            transform: "translateY(".concat(item.start, "px)")
          },
          children: [/*#__PURE__*/_jsx("span", {
            className: styles.logGutter,
            "aria-hidden": "true",
            children: item.index + 1
          }), /*#__PURE__*/_jsx("span", {
            className: styles.logLevel,
            style: {
              color: (_LEVEL_COLOR$entry$le = LEVEL_COLOR[entry.level]) !== null && _LEVEL_COLOR$entry$le !== void 0 ? _LEVEL_COLOR$entry$le : 'var(--lt-text-secondary)'
            },
            children: entry.level
          }), /*#__PURE__*/_jsx("span", {
            className: styles.logText,
            children: entry.text
          }), entry.url && /*#__PURE__*/_jsx("span", {
            className: styles.logSource,
            title: entry.url,
            children: entry.url
          })]
        }, item.index);
      })
    })
  });
}